import { I, Icon } from '@leanin/ui';

import styles from './LearningTrackCoverMosaic.module.css';

type Props = {
  readonly covers: ReadonlyArray<string>;
  readonly title: string;
};

// Cover mosaic for `LearningTrackCard`. Up to 4 cover images from the
// track's first items, rendered in a grid whose layout adapts to how many
// covers are available:
//   0 → tone-tinted placeholder with the book icon
//   1 → single full-bleed image
//   2 → side-by-side, equal halves
//   3 → one large left + two stacked right
//   4 → 2×2 grid
//
// All variants share a 5/2 aspect ratio so cards in the same row stay the
// same height. Inspired by the playlist-tile pattern Spotify / Apple Music
// use for "collection of items" UI, but trimmed to a banner-strip so the
// title + body stay the dominant block.
export function LearningTrackCoverMosaic({ covers, title }: Props) {
  if (covers.length === 0) return <EmptyMosaic />;

  const variant = `cells-${Math.min(covers.length, 4)}` as
    | 'cells-1'
    | 'cells-2'
    | 'cells-3'
    | 'cells-4';

  return (
    <div className={`${styles.mosaic} ${styles[variant]}`} aria-hidden="true">
      {covers.slice(0, 4).map((url, i) => (
        <div
          key={`${url}-${i}`}
          className={styles.cell}
          style={{ backgroundImage: `url("${url}")` }}
          // Empty role + aria-hidden on the wrapper handle a11y — the card
          // title (link aria-label) already names the destination.
          aria-label={`Cover ${i + 1} of ${covers.length} for ${title}`}
        />
      ))}
    </div>
  );
}

function EmptyMosaic() {
  return (
    <div className={`${styles.mosaic} ${styles.empty}`} aria-hidden="true">
      <Icon size={32}>{I.book}</Icon>
    </div>
  );
}
